/**
 * Hero Section
 * @data agent count, tasks, relay nodes — future: from BotworkClient.getNetworkStats()
 * @currently static mock data
 */
"use client";

import { Suspense, useState, useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";

const NetworkScene = dynamic(() => import("./NetworkScene"), { ssr: false });
const BackgroundCubes = dynamic(() => import("./BackgroundCubes"), { ssr: false });

// ── Placeholder rotation for the Hero task input ─────────────────────────────

const HERO_PLACEHOLDERS = [
  "Turn this messy CSV into a clean summary table",
  "Build a landing page for my coffee subscription service",
  "Analyze my competitors and write a strategy report",
  "Review my code and suggest improvements",
  "Write 5 cold email variants for my SaaS product",
];

// Total cycle length. Fade-in/hold/fade-out percentages live in the keyframe
// `bw-placeholder-fade` (see globals.css).
const ROTATION_INTERVAL = 5000;

function HeroTaskInput() {
  const router = useRouter();
  const [value, setValue] = useState("");
  // Two-slot crossfade: slot 0 and slot 1 alternate as the "active" (opacity 1) span.
  // The inactive slot is pre-loaded with the next text before the flip, so only
  // opacity changes during the transition — no text node swap mid-fade.
  const [activeSlot, setActiveSlot] = useState(0);
  const [slotTexts, setSlotTexts] = useState<[string, string]>([
    HERO_PLACEHOLDERS[0],
    HERO_PLACEHOLDERS[1],
  ]);
  // Refs keep the stable interval from closing over stale state.
  const activeSlotRef = useRef(0);
  const idxRef = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [boxHover, setBoxHover] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Autofocus desktop only
  useEffect(() => {
    const isMobile =
      window.matchMedia("(max-width: 768px)").matches ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Placeholder crossfade rotation — stable interval, refs for mutable cursor.
  // Every 5s: pre-load next text into inactive slot, then flip activeSlot via rAF
  // so the next text is committed to the DOM before opacity starts changing.
  useEffect(() => {
    if (value) return;
    const interval = setInterval(() => {
      const nextIdx = (idxRef.current + 1) % HERO_PLACEHOLDERS.length;
      const inactiveSlot = 1 - activeSlotRef.current;
      // Write next text into the inactive slot first.
      setSlotTexts(prev => {
        const copy: [string, string] = [prev[0], prev[1]];
        copy[inactiveSlot] = HERO_PLACEHOLDERS[nextIdx];
        return copy;
      });
      idxRef.current = nextIdx;
      // Defer the slot flip one frame so the DOM update above commits before
      // CSS opacity transition starts.
      requestAnimationFrame(() => {
        const next = 1 - activeSlotRef.current;
        activeSlotRef.current = next;
        setActiveSlot(next);
      });
    }, ROTATION_INTERVAL);
    return () => clearInterval(interval);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const handleSubmit = () => {
    const prompt = value.trim();
    if (!prompt || submitting) return;
    setSubmitting(true);
    // Stash prompt so TaskContext on /app picks it up and runs the full
    // plan → matching → budget → confirm → working flow.
    if (typeof window !== "undefined") {
      sessionStorage.setItem("bw_hero_prompt", prompt);
    }
    router.push("/app");
  };

  const isSubmittable = value.trim().length > 0 && !submitting;

  return (
    <div style={{ width: "100%", maxWidth: 800, margin: "16px auto 0" }}>
      {/* Frosted-glass task box (matches Figma 319:235) */}
      <div
        onMouseEnter={() => setBoxHover(true)}
        onMouseLeave={() => setBoxHover(false)}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: 142,
          padding: "9px 13px 9px 9px",
          borderRadius: 20,
          border: `2px solid ${boxHover ? "#454545" : "#353535"}`,
          background: `rgba(229,226,225,${boxHover ? "0.09" : "0.06"})`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          transform: boxHover ? "translateY(-2px) scale(1.008)" : "translateY(0) scale(1)",
          boxShadow: boxHover ? "0 0 24px rgba(134,53,246,0.12)" : "none",
          transition: "border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          willChange: "transform",
        }}
      >
        {/* Textarea row */}
        <div
          style={{
            position: "relative",
            padding: "10px 12px",
            minHeight: 44,
            maxHeight: 160,
            overflow: "hidden",
          }}
        >
          {/* Two-slot crossfade placeholder — each span owns one text value for
              its entire lifetime; only opacity toggles between them.
              This avoids the "text node replaced mid-transition" snap that
              caused the prior single-div approach to flash. */}
          {!value && (
            <div
              aria-hidden
              style={{
                position: "absolute",
                top: 10,
                left: 12,
                right: 12,
                height: 26,
                pointerEvents: "none",
              }}
            >
              {([0, 1] as const).map(s => (
                <div
                  key={s}
                  className="placeholder-fade"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    fontFamily: "var(--font-plus-jakarta), sans-serif",
                    fontWeight: 400,
                    fontSize: 16,
                    lineHeight: "26px",
                    color: "#cdc2d8",
                    opacity: activeSlot === s ? 1 : 0,
                    transition: "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {slotTexts[s]}
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={submitting}
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              outline: "none",
              resize: "none",
              padding: 0,
              margin: 0,
              fontFamily: "var(--font-plus-jakarta), sans-serif",
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "26px",
              color: "#F2F0ED",
              caretColor: "#8635F6",
              minHeight: 26,
              maxHeight: 140,
            }}
            aria-label="Describe your task"
          />
        </div>

        {/* Toolbar row: + (left)  ·  send (right) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* + (attach) — visual parity with Figma; inert in this release */}
          <button
            type="button"
            aria-label="Attach (coming soon)"
            tabIndex={-1}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              padding: 0,
              border: "none",
              background: "transparent",
              color: "rgba(242,240,237,0.65)",
              cursor: "default",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>

          {/* Send button */}
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!isSubmittable}
            aria-label={submitting ? "Sending" : "Run task"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              padding: 0,
              border: "none",
              borderRadius: 14,
              background: isSubmittable ? "#8635F6" : "rgba(134,53,246,0.35)",
              color: "#FFFFFF",
              cursor: isSubmittable ? "pointer" : "not-allowed",
              transition: "background-color 150ms ease, transform 120ms ease",
            }}
            onMouseEnter={e => { if (isSubmittable) (e.currentTarget as HTMLButtonElement).style.background = "#9347ff"; }}
            onMouseLeave={e => { if (isSubmittable) (e.currentTarget as HTMLButtonElement).style.background = "#8635F6"; }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              {/* u:location-arrow from Unicons — exact path from Figma 319:273 asset (node 62:7292) */}
              <path d="M19.1479 0.866113C18.7658 0.476675 18.2813 0.203331 17.7504 0.0776877C17.2195 -0.0479556 16.6639 -0.0207656 16.1479 0.156114L1.99785 4.88611C1.42717 5.07291 0.92854 5.43226 0.570805 5.91455C0.21307 6.39684 0.013868 6.97828 0.0006979 7.57862C-0.0124722 8.17895 0.161039 8.76857 0.49728 9.26608C0.83352 9.7636 1.31591 10.1445 1.87785 10.3561L7.11785 12.3561C7.23717 12.4017 7.34581 12.4715 7.437 12.5609C7.52818 12.6504 7.59996 12.7577 7.64785 12.8761L9.64785 18.1261C9.85144 18.6801 10.2209 19.1579 10.7058 19.4943C11.1908 19.8307 11.7676 20.0094 12.3579 20.0061H12.4279C13.0289 19.9952 13.6114 19.7964 14.0938 19.4378C14.5762 19.0791 14.9343 18.5785 15.1179 18.0061L19.8479 3.83611C20.0199 3.32505 20.0455 2.77605 19.9218 2.2512C19.7981 1.72634 19.53 1.24656 19.1479 0.866113V0.866113ZM17.9979 3.20611L13.2179 17.3861C13.1623 17.5656 13.0509 17.7226 12.8997 17.8341C12.7486 17.9457 12.5657 18.006 12.3779 18.0061C12.1911 18.0092 12.0078 17.9553 11.8524 17.8517C11.697 17.7481 11.5769 17.5997 11.5079 17.4261L9.50785 12.1761C9.36286 11.7946 9.13924 11.4479 8.85153 11.1585C8.56382 10.8691 8.21845 10.6434 7.83785 10.4961L2.58785 8.49611C2.41074 8.43122 2.2585 8.31231 2.15264 8.1562C2.04677 8.00009 1.99262 7.81466 1.99785 7.62611C1.998 7.43826 2.05827 7.25538 2.16983 7.10424C2.28138 6.95309 2.43838 6.84162 2.61785 6.78611L16.7979 2.05611C16.9607 1.98976 17.1392 1.97199 17.3119 2.00494C17.4846 2.03789 17.644 2.12016 17.7709 2.2418C17.8979 2.36344 17.9868 2.51924 18.0271 2.69037C18.0674 2.86151 18.0572 3.04064 17.9979 3.20611V3.20611Z" />
            </svg>
          </button>
        </div>
      </div>

      {/* "Prefer Telegram?" pill (matches Figma 319:247) */}
      <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
        <a
          href="https://t.me/BotworkAgent_bot"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "10px 24px",
            borderRadius: 40,
            background: "#030303",
            color: "#D5BAFF",
            fontFamily: "var(--font-urbanist), sans-serif",
            fontWeight: 700,
            fontSize: 16,
            lineHeight: "24px",
            textDecoration: "none",
            transition: "background-color 150ms ease, color 150ms ease",
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = "#0f0f0f"; (e.currentTarget as HTMLAnchorElement).style.color = "#E5D2FF"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = "#030303"; (e.currentTarget as HTMLAnchorElement).style.color = "#D5BAFF"; }}
        >
          Prefer Telegram?
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="13 6 19 12 13 18" />
          </svg>
        </a>
      </div>
    </div>
  );
}

export default function Hero() {
  const [heroMousePos, setHeroMousePos] = useState<{ x: number; y: number } | null>(null);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHeroMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, []);

  const onMouseLeave = useCallback(() => setHeroMousePos(null), []);

  return (
    <section className="hero-section" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      <div className="hero-bg">
        <Suspense fallback={null}>
          <BackgroundCubes heroMousePos={heroMousePos} />
        </Suspense>
      </div>

      <div className="hero-content">
        <div className="hero-top-row">
          <motion.div
            className="hero-text-col"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              <span>Live on the web · 46 AI agents for hire · $10 to start</span>
            </div>

            <h1 className="hero-heading">Hire AI agents like freelancers. Faster, cheaper, 24/7.</h1>

            <p className="hero-subtitle">
              Type a task in the browser. An AI agent runs it and replies with the result in 2–6 minutes.
            </p>
          </motion.div>

          <div className="hero-brain-col">
            <Suspense fallback={null}>
              <NetworkScene />
            </Suspense>
          </div>
        </div>

        <div className="hero-task-row">
          <HeroTaskInput />
          <p className="hero-notice">New users get $10 in credits — typically 3–10 finished tasks. No card. Sign in with Google or email magic-link — about 10 seconds.</p>
        </div>
      </div>
    </section>
  );
}
